import { IComboBox, IComboBoxOption, Stack, Text } from "@fluentui/react";
import { useBreakpoint } from "../../../Container/ContainerStyles";
import { BreakpointType } from "../../../Container/ContainerFluid";
import { Row } from "../../../Container/Grid/Row";
import { Col } from "../../../Container/Grid/Col";
import { RegisterComboBox, RegisterTextField } from "../Common/RegisterField";
import { containerStyles, formContainerStyles, lgContainerStyles, lgSubTitleStyles, lgTitleContainerStyles, lgTitleStyles, mdContainerStyles, mdSubTitleStyles, mdTitleContainerStyles, mdTitleStyles, smContainerStyles, smSubTitleStyles, smTitleContainerStyles, smTitleStyles, subTitleStyles, titleContainerStyles, titleStyles, xlContainerStyles, xlSubTitleStyles, xlTitleContainerStyles, xlTitleStyles, xxlContainerStyles, xxlSubTitleStyles, xxlTitleContainerStyles, xxlTitleStyles } from "../Common/Styles/CommonStyles";
import { useEffect, useState } from "react";
import { PostalCode, SystemDataLoadingStatus } from "../../../../../Model/SystemModels";
import { SystemCore } from "../../../../../Core/System/SystemCore";
import { IRegisterContext, RegisterLoader, useRegisterContext } from "../../RegisterContainer";
import { RegisterService } from "../../Service/RegisterService";
import { LeadToLive, LeadToLiveStep } from "../../../../../Model/CommonModel";
import { CountryRegion } from "../../../../../Model/CustomerModels";
import { MessageType, ToastService } from "../../../../../Core/Toast/ToastService";
import { Lead } from "../../../../../Model/PartnerModels";

export const RegisterCompany: React.FC = () => {
    const registerContext: IRegisterContext = useRegisterContext();
    const [dataStatus, setDataStatus] = useState<SystemDataLoadingStatus>(SystemDataLoadingStatus.ToLoad);
    const [leadToLive, setLeadToLive] = useState<LeadToLive>(registerContext.leadToLive);
    const [postalCodes, setPostalCodes] = useState<PostalCode[]>([]);
    const [countryRegionOptions, setCountryRegionOptions] = useState<IComboBoxOption[]>([]);
    const [cityOptions, setCityOptions] = useState<IComboBoxOption[]>([]);
    const [countyOptions, setCountyOptions] = useState<IComboBoxOption[]>([]);
    
    const isSM = useBreakpoint(BreakpointType.sm);
    const isMD = useBreakpoint(BreakpointType.md);
    const isLG = useBreakpoint(BreakpointType.lg);
    const isXL = useBreakpoint(BreakpointType.xl);
    const isXXL = useBreakpoint(BreakpointType.xxl);

    const getPostalCodes = (country: string): Promise<PostalCode[]> => {
        setPostalCodes([]);
        setCityOptions([]);
        setCountyOptions([]);

        return new Promise<PostalCode[]>((resolve, reject) => {
            RegisterService.getPostalCodes(country).then((postalCodeResponse: PostalCode[]) => {
                setPostalCodes(postalCodeResponse);

                resolve(postalCodeResponse);
            })
            .catch((error) => {
                ToastService.showMessage(MessageType.Error, error.message);
                reject(error);
            });
        });
    }
    
    const validateLocationData = (zipCode: string, lead: Lead, postalCodes: PostalCode[]) => {
        let cityOptions: IComboBoxOption[] = [];
        let countyOptions: IComboBoxOption[] = [];
        
        let currentLead: Lead = new Lead();
        currentLead = lead;

        currentLead.zipCode = zipCode;
        currentLead.city = "";
        currentLead.county = "";

        if(zipCode !== ""){
            let postalCodeOptions: PostalCode[] = postalCodes.filter(item => item.zipCode == zipCode);
            postalCodeOptions.forEach((value: PostalCode, index: number, array: PostalCode[]) => {
                if(countyOptions.findIndex(item => item.key == value.county) < 0){
                    countyOptions.push({
                        key: value.county,
                        text: value.county
                    });
                }
    
                cityOptions.push({
                    key: value.city,
                    text: value.city
                });
            })

            if(cityOptions.length == 1){
                currentLead.city = cityOptions[0].key.toString();
            }
            else if(lead.city !== "" && cityOptions.findIndex(item => item.key.toString().toLowerCase() == lead.city.toLowerCase()) >= 0 && cityOptions.length > 0){
                currentLead.city = cityOptions[cityOptions.findIndex(item => item.key.toString().toLowerCase() === lead.city.toLowerCase())].key.toString();
            }

            if(countyOptions.length == 1){
                currentLead.county = countyOptions[0].key.toString();
            }
            else if(lead.county !== "" && countyOptions.findIndex(item => item.key.toString().toLowerCase() == lead.county.toLowerCase()) >= 0 && countyOptions.length > 0){
                currentLead.county = countyOptions[countyOptions.findIndex(item => item.key.toString().toLowerCase() === lead.county.toLowerCase())].key.toString();
            }

        }
        
        setLeadToLive({...leadToLive, lead: currentLead});
        setCityOptions(cityOptions);
        setCountyOptions(countyOptions);
    }


    useEffect(() => {
        if(dataStatus === SystemDataLoadingStatus.ToLoad){
            setDataStatus(SystemDataLoadingStatus.Loading);

            RegisterService.getCountryRegion().then((countryRegionResponse: CountryRegion[]) => {
                setCountryRegionOptions(RegisterService.getCountryRegionOptions(countryRegionResponse));

                //Try get the country from the URL
                let currentLead: Lead = leadToLive.lead;
                if(!currentLead.country)
                    currentLead.country = RegisterService.suggestCountry(leadToLive);

                if(currentLead.country){
                    getPostalCodes(currentLead.country).then((resp) => {
                        let zipCode: string = currentLead.zipCode ? currentLead.zipCode : "";
                        validateLocationData(zipCode, currentLead, resp);
                    })
                    .catch((error) => {
                        ToastService.showMessage(MessageType.Error, error.message);
                    })
                    .finally(() => {
                        setDataStatus(SystemDataLoadingStatus.Loaded);
                    });
                }
                else {
                    setDataStatus(SystemDataLoadingStatus.Loaded);
                }
            })
            .catch((error) => {
                ToastService.showMessage(MessageType.Error, error.message);
                setDataStatus(SystemDataLoadingStatus.Loaded);
            });
        }
    }, [dataStatus]);

    useEffect(() => {
        registerContext.setLeadToLive(leadToLive);
    }, [leadToLive]);

    useEffect(() => {
        if(registerContext.leadToLive.currentStep !== LeadToLiveStep.CompanyData)
            return;

        if(!leadToLive.legalEntityVatManagement) {
            registerContext.setFooterButtonProps({
                ...registerContext.footerButtonProps,
                backButtonDisabled: true,
                backButtonHidden: true,
            })
        }
    }, []);

    if(!SystemCore.isFinishedDataStatus(dataStatus))
        return (
            <RegisterLoader 
                label={RegisterService.translate("dataLoader")} 
            />
        );

    return (
            <Stack
                className={
                    isSM ? 
                        smContainerStyles : 
                    isMD ? 
                        mdContainerStyles :
                    isLG ?
                        lgContainerStyles :
                    isXL ?
                        xlContainerStyles :
                    isXXL ?
                        xxlContainerStyles
                    :
                        containerStyles
                }
            >
                <Stack
                    className={
                        isSM ? 
                            smTitleContainerStyles : 
                        isMD ? 
                            mdTitleContainerStyles :
                        isLG ?
                            lgTitleContainerStyles :
                        isXL ?
                            xlTitleContainerStyles :
                        isXXL ?
                            xxlTitleContainerStyles
                        :
                            titleContainerStyles
                    }
                >
                    <Text 
                        className={
                            isSM ? 
                                smTitleStyles : 
                            isMD ? 
                                mdTitleStyles : 
                            isLG ?
                                lgTitleStyles :
                            isXL ?
                                xlTitleStyles :
                            isXXL ?
                                xxlTitleStyles
                            :
                                titleStyles
                        }
                    >
                        {RegisterService.translate("company:title")}
                </Text>
                <Text 
                    className={
                        isSM ? 
                            smSubTitleStyles : 
                        isMD ? 
                            mdSubTitleStyles :
                        isLG ?
                            lgSubTitleStyles :
                        isXL ?
                            xlSubTitleStyles :
                        isXXL ?
                            xxlSubTitleStyles
                        :
                            subTitleStyles
                    }
                >
                    {RegisterService.translate("company:description")}
                </Text>
            </Stack>
            <Stack
                className={formContainerStyles}
            >
                <Row>
                    <Col>
                        <RegisterTextField 
                            autoFocus
                            placeholder={RegisterService.translate("company:firstname")}
                            value={leadToLive.lead.firstname}
                            errorMessage={RegisterService.getErrorMessage(registerContext.registerError, "firstname")}
                            onChange={(value) => {setLeadToLive({...leadToLive, lead: { ...leadToLive.lead, firstname: value }})}}
                        />
                    </Col>
                    <Col>
                        <RegisterTextField 
                            placeholder={RegisterService.translate("company:lastname")}
                            value={leadToLive.lead.lastname}
                            errorMessage={RegisterService.getErrorMessage(registerContext.registerError, "lastname")}
                            onChange={(value) => {setLeadToLive({...leadToLive, lead: { ...leadToLive.lead, lastname: value }})}}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <RegisterTextField 
                            placeholder={RegisterService.translate("company:companyName")}
                            value={leadToLive.lead.companyName}
                            errorMessage={RegisterService.getErrorMessage(registerContext.registerError, "companyName")}
                            onChange={(value) => {setLeadToLive({...leadToLive, lead: { ...leadToLive.lead, companyName: value }})}}
                        />
                    </Col>
                    <Col>
                        <RegisterComboBox 
                            onChange={(event: React.FormEvent<IComboBox> | null, option?: IComboBoxOption | null, index?: number | null, value?: string) => {
                                getPostalCodes(option?.key as string).finally(() => {
                                    setLeadToLive({...leadToLive, lead: { ...leadToLive.lead, country: option?.key as string }});
                                });
                            }}
                            options={countryRegionOptions}
                            placeholder={RegisterService.translate("company:country")}
                            value={leadToLive.lead.country}
                            errorMessage={RegisterService.getErrorMessage(registerContext.registerError, "country")}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <RegisterTextField 
                            placeholder={RegisterService.translate("company:address")}
                            value={leadToLive.lead.address}
                            onChange={(value) => {setLeadToLive({...leadToLive, lead: { ...leadToLive.lead, address: value }})}}
                            errorMessage={RegisterService.getErrorMessage(registerContext.registerError, "address")}
                        />
                    </Col>
                    <Col>
                        <RegisterTextField
                            onlyNumbers
                            onChange={(value: string) => {
                                console.log(value);
                                validateLocationData(value, leadToLive.lead, postalCodes);
                            }}
                            placeholder={RegisterService.translate("company:zipCode")}
                            value={leadToLive.lead.zipCode}
                            errorMessage={RegisterService.getErrorMessage(registerContext.registerError, "zipCode")}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <RegisterComboBox 
                            onChange={(event: React.FormEvent<IComboBox> | null, option?: IComboBoxOption | null, index?: number | null, value?: string) => {setLeadToLive({...leadToLive, lead: { ...leadToLive.lead, city: value! }})}}
                            options={cityOptions}
                            freeInput={postalCodes.length == 0}
                            placeholder={RegisterService.translate("company:city")}
                            value={leadToLive.lead.city}
                            errorMessage={RegisterService.getErrorMessage(registerContext.registerError, "city")}
                        />
                    </Col>
                    <Col>
                        <RegisterComboBox 
                            onChange={(event: React.FormEvent<IComboBox> | null, option?: IComboBoxOption | null, index?: number | null, value?: string) => {setLeadToLive({...leadToLive, lead: { ...leadToLive.lead, county: value! }})}}
                            options={countyOptions}
                            placeholder={RegisterService.translate("company:county")}
                            freeInput={postalCodes.length == 0}
                            value={leadToLive.lead.county}
                            errorMessage={RegisterService.getErrorMessage(registerContext.registerError, "county")}
                        />
                    </Col>
                </Row>
            </Stack>
        </Stack>
    );
};